import React, { CSSProperties, FC, memo, useState, useEffect } from "react"
import { NewBookingInput, OptionalInputFieldsInput } from "../../../../generated/graphql"
import type { NewBookingInputKeys } from "./bookingInputTypes"
import { Checkbox, Form, Space } from "antd"
import css from "../StepSummary2.module.css"
import ReactMarkdown from "react-markdown"
import classNames from "classnames"
import { CheckCircleTwoTone, CloseCircleTwoTone, InfoCircleTwoTone } from "@ant-design/icons"
import { green, grey, red } from "@ant-design/colors"

type CheckboxItem = {
    id?: string
    label?: string
    description?: string
}

type CustomInputCheckboxGroupProps = {
    field: OptionalInputFieldsInput
    bookingData?: NewBookingInput
    additionalInfoJSON: any
    setAdditionalInfoJSON: (value: any) => void
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    style?: CSSProperties
    handleInputFieldValues?: (values: Array<{ [Key: string]: string | boolean | undefined }>) => void
    showAlert?: boolean
}

const CustomInputCheckboxGroup: FC<CustomInputCheckboxGroupProps> = ({
    field,
    bookingData,
    style,
    additionalInfoJSON,
    setAdditionalInfoJSON,
    updateBookingData,
    handleInputFieldValues,
    showAlert,
}) => {
    const checkboxItems: CheckboxItem[] = (field as any).checkboxItems ?? []
    const minCheckCount = (field as any).minCheckCount ?? 0

    // Helper function to convert stored labels back to IDs
    const labelsToIds = (storedValues: string[]): string[] => {
        return storedValues
            .map((value) => {
                // Check if value is an ID (exists in checkboxItems)
                const byId = checkboxItems.find((item) => item.id === value)
                if (byId) return byId.id ?? ""
                // Otherwise it's a label, find the corresponding ID
                const byLabel = checkboxItems.find((item) => item.label === value)
                return byLabel?.id ?? ""
            })
            .filter(Boolean)
    }

    const [checkedIds, setCheckedIds] = useState<string[]>(() => {
        // Initialize from additionalInfoJSON if available
        const stored = additionalInfoJSON[field.label ?? ""]
        if (Array.isArray(stored)) {
            return labelsToIds(stored)
        }
        return []
    })

    const isValid = checkedIds.length >= minCheckCount
    // F-005: Fehler-Rot erst nach dem ersten Submit-Versuch (showAlert) zeigen.
    const showError = Boolean(showAlert) && !isValid

    useEffect(() => {
        // Sync state when additionalInfoJSON changes externally
        const stored = additionalInfoJSON[field.label ?? ""]
        if (Array.isArray(stored)) {
            setCheckedIds(labelsToIds(stored))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalInfoJSON, field.label])

    const handleCheckboxChange = (itemId: string, checked: boolean) => {
        let newCheckedIds: string[]
        if (checked) {
            newCheckedIds = [...checkedIds, itemId]
        } else {
            newCheckedIds = checkedIds.filter((id) => id !== itemId)
        }
        setCheckedIds(newCheckedIds)

        // Get labels for the selected items (for readable storage in additionalInfo)
        const selectedLabels = newCheckedIds
            .map((id) => {
                const item = checkboxItems.find((i: CheckboxItem) => i.id === id)
                return item?.label
            })
            .filter(Boolean)

        // Update additionalInfoJSON
        const updatedAdditionalInfoJSON = { ...additionalInfoJSON }
        if (field.label) {
            if (selectedLabels.length === 0) {
                delete updatedAdditionalInfoJSON[field.label]
            } else {
                // Store labels instead of IDs for better readability in admin
                updatedAdditionalInfoJSON[field.label] = selectedLabels
            }
            updateBookingData("additionalInfo", JSON.stringify(updatedAdditionalInfoJSON))
            setAdditionalInfoJSON(updatedAdditionalInfoJSON)

            // Update inputFieldValues for validation
            handleInputFieldValues?.([{
                [field.name ?? field.label]: newCheckedIds.length >= minCheckCount
            }])
        }
    }

    const statusColor = isValid ? green[2] : red[2]
    const statusIcon = (intensity?: number, style?: React.CSSProperties) =>
        isValid ? (
            <CheckCircleTwoTone style={style} twoToneColor={green[intensity ?? 2]} />
        ) : showError ? (
            <CloseCircleTwoTone style={style} twoToneColor={red[intensity ?? 2]} />
        ) : (
            // Neutral vor dem ersten Submit-Versuch (kein Fehler-Rot).
            <CloseCircleTwoTone style={style} twoToneColor={grey[2]} />
        )

    const footerText = minCheckCount > 0
        ? `Bitte wähle mindestens ${minCheckCount} der ${checkboxItems.length} Optionen`
        : `${checkedIds.length} von ${checkboxItems.length} ausgewählt`

    return (
        <Form.Item style={style} key={field.name}>
            <div
                className={css.checkboxGroupContainer}
                style={{ border: showError ? `1px solid ${red[2]}` : undefined }}
            >
                {field.label && (
                    <div className={css.checkboxGroupHeadline}>
                        <div className={css.infoIcon}>{statusIcon(3, { fontSize: "1.1em" })}</div>
                        {field.label}
                    </div>
                )}
                {field.description && (
                    <div className={css.checkboxGroupDescription}>
                        <InfoCircleTwoTone className={css.infoIcon} style={{ marginTop: 5}} />
                        <ReactMarkdown
                            skipHtml={false}
                            className={classNames(css.markdown, css.inputDescription, css.checkboxGroupDescriptionText)}
                            components={{
                                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                            }}
                        >
                            {field.description.replace(/(.*)\n---/g, "$1\n\n---\n\n")}
                        </ReactMarkdown>
                    </div>
                )}
                <Space direction="vertical" style={{ width: "100%" }}>
                    {checkboxItems.map((item: CheckboxItem, index: number) => (
                        <div key={item.id ?? index} /* className={css.checkboxGroupItem} */ className={classNames(
                            css.checkboxGroupItem,
                            index >= checkboxItems.length - 1 && css.checkboxGroupItemLast
                        )}>
                            <Checkbox
                                checked={checkedIds.includes(item.id ?? "")}
                                onChange={(e) => handleCheckboxChange(item.id ?? "", e.target.checked)}
                            >
                                <span className={css.checkboxGroupItemLabel}>{item.label}</span>
                            </Checkbox>
                            {item.description && (
                                <div className={css.checkboxGroupItemDescription}>
                                    {item.description}
                                </div>
                            )}
                        </div>
                    ))}
                </Space>
                <div
                    className={classNames(
                        css.checkboxGroupFooter,
                        !showError && css.checkboxGroupFooterHidden
                    )}
                    style={{ backgroundColor: statusColor }}
                >
                    <div className={css.infoIcon}>{statusIcon()}</div>
                    {footerText}
                </div>
            </div>
        </Form.Item>
    )
}

export default memo(CustomInputCheckboxGroup)
